<template>
    <template v-if="page">
        <div class="min-h-dvh tw-relative tw-flex tw-flex-col tw-overflow-hidden">
            <div class="bg-grad tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-10 2xl:tw-right-1/2"></div>
            <div class="io-container tw-relative tw-z-20 tw-my-0 tw-grid tw-flex-grow tw-grid-cols-1 tw-gap-16 tw-py-8 xl:tw-grid-cols-12 3xl:tw-pt-16">
                <div
                    class="tw-relative tw-z-30 xl:tw-col-span-6"
                    :class="page.layout === 'right' ? 'tw-order-1' : 'tw-order-2'">
                    <Tag
                        :color="page.color"
                        :text="page.name" />
                    <img
                        v-if="page.bannerImage"
                        alt=""
                        class="tw-relative tw-left-1/2 tw-my-8 tw-max-h-96 tw-max-w-[calc(100%+100px)] -tw-translate-x-1/2 xl:tw-hidden"
                        :src="useMedia(page.bannerImage).original" />
                    <h1
                        class="tw-mt-6"
                        v-html="page.bannerTitle"></h1>
                    <div
                        class="h3 ckeditor tw-mt-3 md:tw-mt-6 lg:tw-mt-8"
                        v-html="page.bannerDescription"></div>
                </div>
                <div
                    class="tw-relative max-xl:tw-hidden xl:tw-col-span-6"
                    :class="page.layout === 'right' ? 'tw-order-2' : 'tw-order-1'">
                    <img
                        v-if="page.bannerImage"
                        :key="page.bannerImage.documentId"
                        alt=""
                        class="desktop-image tw-absolute tw-inset-0 tw-z-10 tw-mx-auto tw-h-full tw-object-contain tw-object-top max-md:tw-hidden"
                        :src="useMedia(page.bannerImage).original" />
                </div>
            </div>
        </div>
        <div class="io-container radial-grad">
            <h2
                class=""
                v-html="page.contentTitle"></h2>
            <v-expansion-panels
                v-if="page.expansionPanels?.length"
                class="solution-panels">
                <v-expansion-panel
                    v-for="panel in page.expansionPanels"
                    :key="panel.id"
                    elevation="0">
                    <v-expansion-panel-title
                        class="tw-text-lg tw-font-semibold xl:tw-text-2xl"
                        :collapse-icon="collapseClose"
                        :expand-icon="collapseOpen">
                        <div v-html="panel.title"></div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="xl:tw-text-lg">
                        <div
                            class="ckeditor"
                            v-html="panel.text"></div>
                    </v-expansion-panel-text>
                    <v-divider
                        class="tw-my-2"
                        color="dark"
                        opacity="1"
                        thickness="1.5"></v-divider>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div
            v-if="page.products?.length"
            class="io-container">
            <h2
                v-if="page.productsTitle"
                v-html="page.productsTitle"></h2>
            <h2
                v-else
                class=""
                v-html="$t('solution.ourProducts')"></h2>
            <ProductsGrid :products="page.products" />
        </div>
        <div
            v-if="page.projects?.length"
            class="io-container">
            <h2
                v-if="page.projectsTitle"
                class="tw-mb-0"
                v-html="page.projectsTitle"></h2>
            <h2
                v-else
                class="tw-mb-0"
                v-html="$t('solution.relatedProjects')"></h2>
            <ProjectsSlider :projects="page.projects" />
        </div>

        <div
            class="bottom-banner tw-bg-cover tw-bg-center tw-bg-no-repeat tw-py-20"
            :style="{
                backgroundImage: `url(${useMedia(bottomImage).original})`,
            }">
            <div class="io-container tw-relative tw-my-0 max-sm:tw-text-center">
                <h2
                    class="max-md:tw-text-2xl sm:tw-w-8/12"
                    v-html="page.bottomBannerTitle"></h2>
                <v-btn
                    class="tw-h-12"
                    color="blue"
                    rounded="pill"
                    :to="{ name: 'Contacts' }"
                    >{{ $t('solution.contactUs') }}</v-btn
                >
            </div>
        </div>
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script setup lang="ts">
    import Loader from '@/components/Loader.vue';
    import ProductsGrid from '@/components/ProductsGrid.vue';
    import collapseClose from '@/components/icons/collapse-close.vue';
    import collapseOpen from '@/components/icons/collapse-open.vue';
    import Tag from '@/components/Tag.vue';
    import { useAppStore } from '@/store/app';
    import { useMedia } from '@/composables/useMedia';
    import { useWindowSize } from '@vueuse/core';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();
    const $route = useRoute();
    const $router = useRouter();
    const appStore = useAppStore();

    const { width, height } = useWindowSize();

    const page = computed(() => {
        return appStore.getSolutionBySlug($route.params.slug as string);
    });

    const pageColor = computed(() => (page.value ? `rgb(var(--v-theme-${page.value.color}))` : ''));

    const bottomImage = computed(() => {
        if (width.value < 768 && page.value?.bottomBannerImageMobile) {
            return page.value?.bottomBannerImageMobile;
        }
        return page.value?.bottomBannerImage;
    });

    watchEffect(() => {
        if (!page.value && appStore.hasFinishedLoading) {
            $router.push({ name: 'NotFound' });
        }
    });

    watchEffect(() => {
        // set the css variable --page-color
        if (pageColor.value) {
            document.documentElement.style.setProperty('--page-color', pageColor.value);
        }
    });

    watchEffect(() => {
        if (page.value?.name) {
            document.title = page.value?.name + ' - IOTech';
        } else {
            document.title = t('solution.docTitle');
        }
    });
</script>

<style lang="scss">
    .solution-panels {
        button.v-expansion-panel-title,
        .v-expansion-panel-text__wrapper {
            padding-inline: 0 !important;
        }

        .v-expansion-panel-text__wrapper {
            @apply tw-pt-4;
        }

        .v-expansion-panel::after {
            @apply tw-opacity-0;
        }

        .v-expansion-panel {
            background: unset;
        }
    }
</style>

<style scoped lang="scss">
    .bottom-banner {
        @apply tw-relative;

        & > * {
            @apply tw-z-10;
        }

        &::before {
            @apply tw-absolute tw-inset-0;
            content: '';
            z-index: 1;
            // background: url('@/assets/temp/ds.png');
            background-position: center;
            background-size: cover;
        }
    }
</style>
